import React, { useRef, useState, Suspense, useEffect, ReactNode, Component, useCallback, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Center, Text, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import '../../ModelPreview.css';
import { ModelInfo } from '../index';
import { isValidURL } from '../validators';
import { CameraAdjust } from '../CameraAdjust';
import { CustomBox3Helper } from '../CustomBox3Helper';
import { MemoizedModel } from '../Model';
import { 
  formatDimensionWithUnits as formatDimensionWithUnitsUtil,
  parseDimensionWithUnits as parseDimensionWithUnitsUtil,
  getDimensionDisplayValue as getDimensionDisplayValueUtil,
  isValueChanged as isValueChangedUtil
} from './dimensionUtils';
import { createDimensionEventHandlers } from './dimensionEventHandlers';
import { 
  getExtendedDimensionsFromStorage as getExtendedDimensionsFromStorageUtil,
  saveModelChanges as saveModelChangesUtil,
  resetOriginalValues as resetOriginalValuesUtil,
  getActiveVersionName as getActiveVersionNameUtil
} from './modelStorageUtils';
import { 
  downloadFile as downloadFileUtil,
  handleError as handleErrorUtil,
  handleCanvasError as handleCanvasErrorUtil,
  createWebGLContextErrorHandler
} from './fileDownloadUtils';
import { 
  initializeDuplicateCount,
  handleDuplicateChange as handleDuplicateChangeUtil,
  handleDuplicateIncrement as handleDuplicateIncrementUtil,
  handleDuplicateDecrement as handleDuplicateDecrementUtil
} from './duplicateCountUtils';
import { 
  resetView as resetViewUtil,
  createViewportObserver,
  CameraControlsRef
} from './cameraControlUtils';

// Simple ErrorBoundary class component
class ModelErrorBoundary extends Component<{children: ReactNode, fallback: ReactNode}> {
  state = { hasError: false };
  
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  
  componentDidCatch(error: Error) {
    console.error("Error caught by boundary:", error);
  }
  
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    
    return this.props.children;
  }
}

// Component that manages dimension lines display after model positioning
function BoundingBoxManager({ children }: { children: ReactNode }) {
  const { scene } = useThree();
  const [modelBbox, setModelBbox] = useState<THREE.Box3 | null>(null);
  
  useEffect(() => {
    // Wait a bit for CameraAdjust to position the model properly
    const timer = setTimeout(() => {
      if (scene.children.length > 0) {
        try {
          // Calculate bounding box for the current scene
          const box = new THREE.Box3().setFromObject(scene);
          
          // Only update if the box is valid and has actual size
          const size = box.getSize(new THREE.Vector3());
          if (size.length() > 0) {
            setModelBbox(box.clone());
          }
        } catch (error) {
          console.error("Error calculating bounding box:", error);
        }
      }
    }, 200); // Small delay to ensure model is positioned
    
    return () => clearTimeout(timer);
  }, [scene]);
  
  return (
    <>
      {children}
      {modelBbox && (
        <DimensionLines box={modelBbox} />
      )}
    </>
  );
}

// Component to render dimension lines along X, Y, Z axes
function DimensionLines({ box }: { box: THREE.Box3 }): React.JSX.Element {
  const lines = useMemo(() => {
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const min = box.min;
    const max = box.max;
    
    const lineObjects: THREE.Group[] = [];
    
    // Create dimension lines for each axis
    const createDimensionLine = (
      start: THREE.Vector3, 
      end: THREE.Vector3, 
      color: string, 
      label: string,
      dimension: number
    ) => {
      const group = new THREE.Group();
      
      // Main dimension line
      const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
      const material = new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        linewidth: 2,
        transparent: true,
        opacity: 0.3
      });
      const line = new THREE.Line(geometry, material);
      group.add(line);
      
      // Small perpendicular lines at ends (tick marks)
      const tickLength = Math.max(size.x, size.y, size.z) * 0.02; // 2% of largest dimension
      
      // Start tick
      const startTick = createTickMark(start, end, tickLength, color);
      group.add(startTick);
      
      // End tick
      const endTick = createTickMark(end, start, tickLength, color);
      group.add(endTick);
      
      return group;
    };
    
    const createTickMark = (
      position: THREE.Vector3, 
      direction: THREE.Vector3, 
      length: number, 
      color: string
    ) => {
      const dir = direction.clone().sub(position).normalize();
      const perpendicular = new THREE.Vector3();
      
      // Find a perpendicular vector
      if (Math.abs(dir.x) < 0.9) {
        perpendicular.set(1, 0, 0);
      } else {
        perpendicular.set(0, 1, 0);
      }
      perpendicular.cross(dir).normalize().multiplyScalar(length);
      
      const tickStart = position.clone().add(perpendicular);
      const tickEnd = position.clone().sub(perpendicular);
      
      const tickGeometry = new THREE.BufferGeometry().setFromPoints([tickStart, tickEnd]);
      const tickMaterial = new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        linewidth: 2,
        transparent: true,
        opacity: 0.3
      });
      
      return new THREE.Line(tickGeometry, tickMaterial);
    };
    
    // X-axis (Width) - Red line
    const widthStart = new THREE.Vector3(min.x, min.y - size.y * 0.1, min.z);
    const widthEnd = new THREE.Vector3(max.x, min.y - size.y * 0.1, min.z);
    const widthLine = createDimensionLine(widthStart, widthEnd, '#cc8888', 'W', size.x);
    lineObjects.push(widthLine);
    
    // Y-axis (Depth) - Green line  
    const depthStart = new THREE.Vector3(max.x + size.x * 0.1, min.y, min.z);
    const depthEnd = new THREE.Vector3(max.x + size.x * 0.1, max.y, min.z);
    const depthLine = createDimensionLine(depthStart, depthEnd, '#88cc88', 'D', size.y);
    lineObjects.push(depthLine);
    
    // Z-axis (Height) - Blue line
    const heightStart = new THREE.Vector3(max.x + size.x * 0.1, min.y - size.y * 0.1, min.z);
    const heightEnd = new THREE.Vector3(max.x + size.x * 0.1, min.y - size.y * 0.1, max.z);
    const heightLine = createDimensionLine(heightStart, heightEnd, '#8888cc', 'H', size.z);
    lineObjects.push(heightLine);
    
    return lineObjects;
  }, [box]);

  // Calculate text positions and data
  const textData = useMemo(() => {
    const size = new THREE.Vector3();
    box.getSize(size);
    const min = box.min;
    const max = box.max;
    
    // Calculate font size based on model size
    const fontSize = Math.max(size.x, size.y, size.z) * 0.03;
    
    // Calculate offset for text positioning (so it's not directly on the line)
    const textOffset = Math.max(size.x, size.y, size.z) * 0.03;
    
    return [
      {
        position: [
          (min.x + max.x) / 2, // Center X
          min.y - size.y * 0.1 - textOffset, // Width line Y with offset below
          min.z + textOffset // Width line Z with slight forward offset
        ] as [number, number, number],
        text: 'W',
        color: '#cc8888',
        fontSize
      },
      {
        position: [
          max.x + size.x * 0.1 + textOffset, // Depth line X with offset outward
          (min.y + max.y) / 2, // Center Y
          min.z + textOffset // Depth line Z with slight forward offset
        ] as [number, number, number],
        text: 'D',
        color: '#88cc88',
        fontSize
      },
      {
        position: [
          max.x + size.x * 0.1 + textOffset, // Height line X with offset outward (matching new line position)
          min.y - size.y * 0.1 - textOffset, // Height line Y with offset downward (matching new line position)
          (min.z + max.z) / 2 // Center Z
        ] as [number, number, number],
        text: 'H',
        color: '#8888cc',
        fontSize
      }
    ];
  }, [box]);

  useEffect(() => {
    return () => {
      // Cleanup geometries and materials
      lines.forEach(line => {
        line.traverse((child) => {
          if (child instanceof THREE.Line) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          }
        });
      });
    };
  }, [lines]);

  return (
    <>
      {lines.map((line, index) => (
        <primitive key={index} object={line} />
      ))}
      {textData.map((data, index) => (
        <Billboard
          key={`billboard-${index}`}
          position={data.position}
        >
          <Text
            fontSize={data.fontSize}
            color={data.color}
            anchorX="center"
            anchorY="middle"
          >
            {data.text}
          </Text>
        </Billboard>
      ))}
    </>
  );
}

// ModelCard component with selection checkbox
function ModelCard({ model, isSelected, onToggleSelect }: { 
  model: ModelInfo; 
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const cameraControlsRef = useRef<any>(null);
  
  // Add state for visibility tracking
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Add state for dimensions and units
  const [dimensions, setDimensions] = useState<{ x: number, y: number, z: number } | null>(null);
  const [useInches, setUseInches] = useState(false);
  
  // Add state for tracking editing of individual dimension inputs
  const [editingDimensions, setEditingDimensions] = useState<{
    x: string | null;
    y: string | null;
    z: string | null;
  }>({ x: null, y: null, z: null });
  
  // Add state for duplicate count
  const [duplicateCount, setDuplicateCount] = useState(model.duplicates || 1);
  
  // Flag to track if we've loaded dimensions from storage
  const [loadedFromStorage, setLoadedFromStorage] = useState(false);
  
  // Local helper function for extended dimension data
  const getExtendedDimensionsFromStorage = (modelId: string) => {
    return getExtendedDimensionsFromStorageUtil(modelId);
  };

  // Initialize dimensions, versions if needed, and load saved dimensions
  useEffect(() => {
    // Try to load saved dimensions from localStorage
    const savedData = getExtendedDimensionsFromStorage(model.id);
    
    if (savedData) {
      // Set current dimensions from saved data
      setDimensions(savedData.dimensions);
      
      // Always initialize original dimensions if they don't exist
      if (!model.originalDimensions) {
        // Use saved dimensions as original if that's all we have
        model.originalDimensions = { ...savedData.trueOriginalDimensions };
      }
      
      // Create or update version with saved dimensions
      if (!model.versions || model.versions.length === 0) {
        model.versions = [{
          name: 'V1',
          scale: savedData.scale,
          dimensions: savedData.dimensions
        }];
        model.activeVersionIndex = 0;
      } else {
        // Update the active version
        const currentIndex = model.activeVersionIndex || 0;
        model.versions[currentIndex].dimensions = savedData.dimensions;
        model.versions[currentIndex].scale = savedData.scale;
      }
      
      setLoadedFromStorage(true);
    }
  }, [model.id]); // Only run once when model id changes
  
  // Initialize versions array if not present
  useEffect(() => {
    if (!model.versions && dimensions) {
      // Store original dimensions
      model.originalDimensions = { ...dimensions };
      // Initialize with original version
      model.versions = [
        {
          name: 'V1',
          scale: 1.0,
          dimensions: { ...dimensions }
        }
      ];
      model.activeVersionIndex = 0;
    }
  }, [dimensions, model]);
  
  // Initialize duplicates from model or set to 1
  useEffect(() => {
    initializeDuplicateCount(model, setDuplicateCount);
  }, [model]);
  
  // Update model duplicates when count changes
  const handleDuplicateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleDuplicateChangeUtil(e, model, setDuplicateCount);
  };

  // Handle increment/decrement buttons
  const handleDuplicateIncrement = () => {
    handleDuplicateIncrementUtil(duplicateCount, model, setDuplicateCount);
  };

  const handleDuplicateDecrement = () => {
    handleDuplicateDecrementUtil(duplicateCount, model, setDuplicateCount);
  };
  
  // Format dimensions to display with one decimal place
  const formatDimensionWithUnits = (value: number): string => {
    return formatDimensionWithUnitsUtil(value, useInches);
  };
  
  // Parse dimension input considering current unit
  const parseDimensionWithUnits = (value: string): number => {
    return parseDimensionWithUnitsUtil(value, useInches);
  };
  

  
  // Get the display value for a dimension input
  const getDimensionDisplayValue = (axis: 'x' | 'y' | 'z'): string => {
    return getDimensionDisplayValueUtil(axis, editingDimensions, dimensions, useInches);
  };
  
  // Handle scaling a model based on target dimension
  const handleScale = (axis: 'x' | 'y' | 'z', newValue: number) => {
    if (!dimensions) {
      return;
    }
    
    // If originalDimensions is missing, create it from current dimensions
    if (!model.originalDimensions) {
      model.originalDimensions = { ...dimensions };
    }
    
    // Calculate scale factor based on original dimensions
    const originalValue = model.originalDimensions[axis];
    const scaleFactor = newValue / originalValue;
    
    if (scaleFactor <= 0) {
      // Only prevent negative or zero scaling
      alert(t('gcodeGeneration.errors.scaleFactor'));
      return;
    }
    
    // Create scaled dimensions with uniform scaling
    const scaledDimensions = {
      x: model.originalDimensions.x * scaleFactor,
      y: model.originalDimensions.y * scaleFactor,
      z: model.originalDimensions.z * scaleFactor
    };
    
    // Don't create a new version, update the current one
    if (!model.versions || model.versions.length === 0) {
      // If no versions exist, create the first one
      model.versions = [{
        name: 'V1',
        scale: scaleFactor,
        targetDimension: {
          value: newValue,
          axis: axis
        },
        dimensions: scaledDimensions
      }];
      model.activeVersionIndex = 0;
    } else {
      // Update the current version
      const currentIndex = model.activeVersionIndex || 0;
      model.versions[currentIndex].scale = scaleFactor;
      model.versions[currentIndex].targetDimension = {
        value: newValue,
        axis: axis
      };
      model.versions[currentIndex].dimensions = scaledDimensions;
    }
    
    // Update the current dimensions to show the scaled values
    setDimensions(scaledDimensions);
    
    // Save the changes to persistent storage
    saveModelChanges(model);
  };

  // Create dimension event handlers
  const dimensionHandlers = createDimensionEventHandlers({
    dimensions,
    useInches,
    setEditingDimensions,
    handleScale
  });
  
  // Extract handlers for easier access
  const {
    toggleUnits: toggleUnitsHandler,
    handleDimensionFocus,
    handleDimensionChange,
    handleDimensionSubmit: handleDimensionSubmitBase,
    handleDimensionKeyPress: handleDimensionKeyPressBase
  } = dimensionHandlers;
  
  // Wrapper functions to maintain the same interface
  const toggleUnits = () => toggleUnitsHandler(setUseInches);
  const handleDimensionSubmit = (axis: 'x' | 'y' | 'z') => 
    handleDimensionSubmitBase(axis, editingDimensions);
  const handleDimensionKeyPress = (axis: 'x' | 'y' | 'z', e: React.KeyboardEvent) => 
    handleDimensionKeyPressBase(axis, e, editingDimensions);
  
  // Function to save model changes to persistent storage
  const saveModelChanges = async (modelToSave: ModelInfo) => {
    await saveModelChangesUtil(modelToSave, dimensions);
  };
  
  // Handle dimensions calculation callback from Model component
  const handleDimensionsCalculated = useCallback((newDimensions: { x: number, y: number, z: number }) => {
    // Always store original dimensions when we get calculated ones
    if (!model.originalDimensions) {
      model.originalDimensions = { ...newDimensions };
      
      // When we first calculate dimensions, save them as the true original dimensions
      const storageKey = `model_dimensions_${model.id}`;
      try {
        const existingDataString = localStorage.getItem(storageKey);
        if (!existingDataString) {
          // Save these initial dimensions as both current and true original
          const initialSaveData = {
            dimensions: newDimensions,
            scale: 1.0,
            timestamp: new Date().toISOString(),
            modelId: model.id,
            trueOriginalDimensions: newDimensions
          };
          localStorage.setItem(storageKey, JSON.stringify(initialSaveData));
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    // If we've already loaded dimensions from storage, don't override with calculated ones
    if (loadedFromStorage) {
      return;
    }
    
    setDimensions(prev => {
      // Only update dimensions if they've changed or are null
      if (!prev || 
          prev.x !== newDimensions.x || 
          prev.y !== newDimensions.y || 
          prev.z !== newDimensions.z) {
        
        // Check localStorage for saved dimensions
        const savedData = getExtendedDimensionsFromStorage(model.id);
        
        if (savedData) {
          // Set original dimensions if not already set
          if (!model.originalDimensions) {
            model.originalDimensions = { ...savedData.trueOriginalDimensions };
          }
          
          // Create a version with the saved dimensions if needed
          if (!model.versions) {
            model.versions = [
              {
                name: 'V1',
                scale: savedData.scale,
                dimensions: savedData.dimensions
              }
            ];
            model.activeVersionIndex = 0;
          }
          
          setLoadedFromStorage(true);
          return savedData.dimensions;
        }
        
        // Store original dimensions if not already set
        if (!model.originalDimensions) {
          model.originalDimensions = { ...newDimensions };
          
          // Initialize with original version if not already set
          if (!model.versions) {
            model.versions = [
              {
                name: 'V1',
                scale: 1.0,
                dimensions: { ...newDimensions }
              }
            ];
            model.activeVersionIndex = 0;
          }
        }
        
        return newDimensions;
      }
      return prev;
    });
  }, [model, loadedFromStorage]);
  
  // Handle loading errors
  const handleError = () => {
    handleErrorUtil(setError, setLoading);
  };
  
  // Function to reset camera view
  const resetView = () => {
    resetViewUtil(cameraControlsRef);
  };
  
  // Function to reset dimensions to original values
  const resetOriginalValues = () => {
    resetOriginalValuesUtil(model, setDimensions);
    // Save the changes to persistent storage
    saveModelChanges(model);
  };
  
  // Check if the model card is visible in viewport
  useEffect(() => {
    return createViewportObserver(setIsVisible, cardRef);
  }, []);
  
  // Download a file (GLB or 3MF)
  const downloadFile = async (fileType: 'glb' | 'three_mf', extension: string) => {
    await downloadFileUtil(model.id, model.filename, fileType, extension, t);
  };
  
  // Avoid error handling in React by catching errors outside the component
  useEffect(() => {
    const handleWebGLContextError = createWebGLContextErrorHandler(setError, setLoading);
    
    window.addEventListener('error', handleWebGLContextError);
    return () => window.removeEventListener('error', handleWebGLContextError);
  }, []);
  
  // Add error boundary using try-catch in Canvas - correct type definition
  const handleCanvasError = (event: any) => {
    handleCanvasErrorUtil(event, setError, setLoading);
  };
  
  // Function to validate URL before trying to load it

  
  // Get the active version name
  const getActiveVersionName = () => {
    return getActiveVersionNameUtil(model);
  };
  
  // Check if a dimension value has changed from original
  const isValueChanged = (axis: 'x' | 'y' | 'z'): boolean => {
    return isValueChangedUtil(axis, dimensions, model.originalDimensions, model.id);
  };
  
  // Check if model URL is actually valid
  if (model.glbUrl && !isValidURL(model.glbUrl)) {
    return (
      <div className="model-card" ref={cardRef}>
        <div className="preview-placeholder error">
          <p>{t('modelCard.preview.errorInvalid')}</p>
        </div>
        {model.versions && model.activeVersionIndex !== undefined && model.activeVersionIndex > 0 && (
          <div className="model-info">
            <p className="model-version">
              {getActiveVersionName()} ({(model.versions[model.activeVersionIndex].scale * 100).toFixed(0)}%)
            </p>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className={`model-card ${isSelected ? 'selected' : ''}`} ref={cardRef}>
      {onToggleSelect && (
        <label className="select-checkbox-container">
          <input 
            type="checkbox" 
            checked={isSelected} 
            onChange={onToggleSelect}
            id={`select-model-${model.id}`}
          />
          <span className="checkmark"></span>
        </label>
      )}
      
      <div className="model-viewer">
        {model.glbUrl ? (
          <>
            {error ? (
              <div className="preview-placeholder error">
                <p>{t('modelCard.preview.error', { message: error })}</p>
              </div>
            ) : (
              <>
                {isVisible ? (
                  <Canvas 
                    shadows={false}
                    dpr={[0.6, 1.0]}
                    camera={{ 
                      position: [15, 15, 15],
                      up: [0, 0, 1],
                      fov: 40,
                      near: 0.1,
                      far: 10000
                    }}
                    style={{ background: '#ffffff' }}
                    flat={true}
                    onCreated={({ camera, gl, scene }) => {
                      setLoading(false);
                      scene.background = new THREE.Color(0xffffff);
                      
                      // Remove any helper objects that might be in the scene
                      scene.children.forEach(child => {
                        if (child.type && 
                            (child.type.includes('Helper') || 
                             child.type.includes('Grid') || 
                             child.type.includes('Circle'))) {
                          scene.remove(child);
                        }
                      });
                      
                      // Store the controls reference in camera userData for CameraAdjust to use
                      camera.userData.controls = null;
                      
                      // Ensure camera is looking at the center (0,0,0)
                      camera.lookAt(0, 0, 0);
                      
                      // Configure renderer
                      gl.setPixelRatio(window.devicePixelRatio * 0.85); // Reduce for better performance
                      gl.setClearColor(0xffffff);
                      gl.outputEncoding = THREE.sRGBEncoding;
                      
                      // Ensure depth testing is properly configured
                      gl.shadowMap.enabled = false;
                      gl.toneMappingExposure = 1;
                      gl.toneMapping = THREE.NoToneMapping;
                      
                      // Ensure proper depth testing
                      gl.localClippingEnabled = true;
                    }}
                    gl={{ 
                      powerPreference: 'default',
                      antialias: false,
                      stencil: false,
                      depth: true,
                      alpha: true
                    }}
                    onError={handleCanvasError}
                    frameloop="always"
                  >
                    {/* Lighting without shadows */}
                    <ambientLight intensity={1.0} />
                    <directionalLight 
                      position={[10, 10, 10]} 
                      intensity={1.5} 
                      castShadow={false}
                    />
                    <directionalLight position={[-10, -5, -10]} intensity={1.0} />
                    <directionalLight position={[0, -10, 0]} intensity={0.8} color="#ffffff" />
                    <Suspense fallback={null}>
                      <ModelErrorBoundary fallback={
                        <mesh>
                          <boxGeometry args={[2, 2, 2]} />
                          <meshStandardMaterial color="#07F77F" />
                          <Text position={[0, 0, 2]} fontSize={0.5} color="white">
                            {t('modelCard.preview.failed')}
                          </Text>
                        </mesh>
                      }>
                        <color attach="background" args={['#ffffff']} />
                        <BoundingBoxManager>
                          <CameraAdjust>
                            <Center scale={1.0}>
                              <MemoizedModel 
                                url={model.glbUrl} 
                                modelId={model.id}
                                onDimensionsCalculated={handleDimensionsCalculated}
                              />
                            </Center>
                          </CameraAdjust>
                        </BoundingBoxManager>
                      </ModelErrorBoundary>
                      <OrbitControls 
                        ref={cameraControlsRef}
                        makeDefault
                        enablePan={true} 
                        enableZoom={true} 
                        enableRotate={true}
                        minDistance={0.05}
                        maxDistance={1000}
                        zoomSpeed={1.5}
                        panSpeed={1.5}
                        enableDamping={true}
                        dampingFactor={0.05}
                        onUpdate={(self) => {
                          if (self.object && self.object.userData) {
                            self.object.userData.controls = self;
                          }
                        }}
                      />
                    </Suspense>
                  </Canvas>
                ) : (
                  <div className="preview-placeholder">
                    <p>{t('modelCard.preview.title')}</p>
                    <p>{t('modelCard.preview.scrollToView')}</p>
                  </div>
                )}
                <button className="reset-view-button" onClick={resetOriginalValues}>
                  {t('modelCard.controls.resetValues')}
                </button>
                
                {/* Version indicator below reset view */}
                {model.versions && model.activeVersionIndex !== undefined && model.activeVersionIndex > 0 && (
                  <div className="active-version-indicator">
                    {getActiveVersionName()} ({(model.versions[model.activeVersionIndex].scale * 100).toFixed(0)}%)
                  </div>
                )}
                
                {/* Filename display */}
                <div className="filename-display">
                  <span className="filename-text" title={model.filename}>
                    {model.filename}
                  </span>
                </div>
                
                {/* Dimensions display at bottom with direct editing */}
                {dimensions && (
                  <div className="model-controls-wrapper">
                  <div className="dimensions-display">
                    <div className="dimension-item">
                      <span>{t('modelCard.dimensions.width')}</span>
                      <input
                        type="text"
                        value={getDimensionDisplayValue('x')}
                        onChange={(e) => handleDimensionChange('x', e.target.value)}
                        onFocus={(e) => handleDimensionFocus('x', e)}
                        onBlur={() => handleDimensionSubmit('x')}
                        onKeyPress={(e) => handleDimensionKeyPress('x', e)}
                        className={`dimension-input-direct ${isValueChanged('x') ? 'value-changed' : ''}`}
                      />
                      <span className="dimension-unit">{useInches ? 'in' : 'mm'}</span>
                    </div>
                    <div className="dimension-item">
                      <span>{t('modelCard.dimensions.depth')}</span>
                      <input
                        type="text"
                        value={getDimensionDisplayValue('y')}
                        onChange={(e) => handleDimensionChange('y', e.target.value)}
                        onFocus={(e) => handleDimensionFocus('y', e)}
                        onBlur={() => handleDimensionSubmit('y')}
                        onKeyPress={(e) => handleDimensionKeyPress('y', e)}
                        className={`dimension-input-direct ${isValueChanged('y') ? 'value-changed' : ''}`}
                      />
                      <span className="dimension-unit">{useInches ? 'in' : 'mm'}</span>
                    </div>
                    <div className="dimension-item">
                      <span>{t('modelCard.dimensions.height')}</span>
                      <input
                        type="text"
                        value={getDimensionDisplayValue('z')}
                        onChange={(e) => handleDimensionChange('z', e.target.value)}
                        onFocus={(e) => handleDimensionFocus('z', e)}
                        onBlur={() => handleDimensionSubmit('z')}
                        onKeyPress={(e) => handleDimensionKeyPress('z', e)}
                        className={`dimension-input-direct ${isValueChanged('z') ? 'value-changed' : ''}`}
                      />
                      <span className="dimension-unit">{useInches ? 'in' : 'mm'}</span>
                    </div>
                    <button 
                      className="unit-toggle-btn"
                      onClick={toggleUnits}
                      title={useInches ? t('modelCard.controls.switchToMm') : t('modelCard.controls.switchToInches')}
                    >
                      {useInches ? 'in' : 'mm'}
                    </button>
                  </div>
                 
                  {/* Duplicate control below dimensions */}
                  <div className="duplicate-control" title={t('modelCard.controls.copiesLabel')}>
                    <span className="copies-label">{t('modelCard.controls.copiesLabel')}</span>
                    <input
                      id={`duplicate-count-${model.id}`}
                      type="number"
                      min="1"
                      value={duplicateCount}
                      onChange={handleDuplicateChange}
                      className="duplicate-input"
                    />
                    <button 
                      className="duplicate-arrow-btn"
                      onClick={handleDuplicateIncrement}
                      title={t('modelCard.controls.increaseCopies')}
                    >
                      <svg width="8" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5L5 1L1 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      className="duplicate-arrow-btn"
                      onClick={handleDuplicateDecrement}
                      disabled={duplicateCount <= 1}
                      title={t('modelCard.controls.decreaseCopies')}
                    >
                      <svg width="8" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  </div>
                )}
              </>
            )}
            {loading && isVisible && (
              <div className="loading-overlay">
                <div className="classic-spinner-small"></div>
                <p>{t('modelCard.preview.loading')}</p>
              </div>
            )}
          </>
        ) :
          <div className="preview-placeholder">
            <p>{t('modelCard.preview.title')}</p>
            <p>{t('modelCard.preview.loadingPreview')}</p>
          </div>
        }
      </div>
      
      {model.versions && model.activeVersionIndex !== undefined && model.activeVersionIndex > 0 && (
        <div className="model-info">
          <p className="model-version">
            {getActiveVersionName()} ({(model.versions[model.activeVersionIndex].scale * 100).toFixed(0)}%)
          </p>
        </div>
      )}
      
      {/* Filename display */}
      <div className="filename-display">
        <span className="filename-text" title={model.filename}>
          {model.filename}
        </span>
      </div>
    </div>
  );
}

export default ModelCard; 